import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Plus, ExternalLink, Trash2, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Documents() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = user.role === "admin" || user.role === "manager";
  const load = () => api.get("/documents").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Remove document?")) return; await api.delete(`/documents/${id}`); load(); };
  const grouped = {};
  rows.forEach((d) => { (grouped[d.category] = grouped[d.category] || []).push(d); });
  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">HR & Skills</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Documents</h1>
          <p className="text-sm text-slate-400 mt-1">Link cloud files or upload PDFs / DOCX so the AI can answer questions from their contents.</p>
        </div>
        {canManage && <button onClick={() => setOpen(true)} data-testid="new-doc" className="px-4 py-2.5 rounded-lg font-bold text-black inline-flex items-center gap-2" style={{ background: "var(--accent)" }}><Plus size={16} /> Add document</button>}
      </div>
      {Object.entries(grouped).map(([cat, docs]) => (
        <section key={cat}>
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{cat}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-${d.id}`} className="card-surface p-4 flex items-start gap-3 hover:border-[color:var(--accent)]/40">
                <FileText size={20} className="text-[color:var(--accent)] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  {d.url ? (
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="font-semibold hover:text-[color:var(--accent)] flex items-center gap-1">{d.title} <ExternalLink size={12} /></a>
                  ) : (
                    <div className="font-semibold flex items-center gap-2">{d.title}{d.source_filename && <span className="text-[10px] text-slate-500 uppercase tracking-widest">{d.source_filename.split(".").pop()}</span>}</div>
                  )}
                  {d.description && <div className="text-xs text-slate-400 mt-1">{d.description}</div>}
                  <div className="flex items-center gap-2 mt-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Added by {d.uploaded_by_name}</div>
                    {d.has_content && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[color:var(--accent)]" title="Indexed for AI Q&A"><Sparkles size={10} /> AI-searchable</span>}
                  </div>
                </div>
                {canManage && <button onClick={() => del(d.id)} className="text-slate-500 hover:text-[#ff3b5c]" data-testid={`doc-del-${d.id}`}><Trash2 size={14} /></button>}
              </div>
            ))}
          </div>
        </section>
      ))}
      {rows.length === 0 && <div className="card-surface p-10 text-center text-slate-500"><FileText size={24} className="inline mr-2" /> No documents yet.</div>}
      {open && <NewDoc onClose={() => setOpen(false)} onDone={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function NewDoc({ onClose, onDone }) {
  const [mode, setMode] = useState("link"); // link | paste | upload
  const [f, setF] = useState({ title: "", url: "", description: "", category: "General", content: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inp = "w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "upload") {
        if (!file) throw new Error("Choose a file (PDF, DOCX, TXT, or MD)");
        const data_base64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
        await api.post("/documents/upload", { title: f.title, description: f.description, category: f.category, filename: file.name, data_base64 });
      } else {
        const payload = { title: f.title, description: f.description, category: f.category };
        if (mode === "link") payload.url = f.url;
        if (mode === "paste") payload.content = f.content;
        await api.post("/documents", payload);
      }
      onDone();
      toast.success("Document added");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="doc-modal">
        <h2 className="text-xl font-bold mb-4">Add document</h2>
        <div className="flex gap-2 mb-4">
          {[
            { k: "link", label: "Cloud link" },
            { k: "paste", label: "Paste text" },
            { k: "upload", label: "Upload file" },
          ].map((t) => (
            <button key={t.k} type="button" onClick={() => setMode(t.k)} data-testid={`doc-mode-${t.k}`}
              className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-widest ${mode === t.k ? "text-black" : "border border-[#2a3447] text-slate-300"}`}
              style={mode === t.k ? { background: "var(--accent)" } : {}}>
              {t.label}
            </button>
          ))}
        </div>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Title *</div><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="doc-title" className={inp} /></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Category</div><input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="doc-category" className={inp} placeholder="e.g. Policies, Guides" /></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Description</div><textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="doc-desc" className={inp + " resize-none"} /></label>
        {mode === "link" && (
          <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">URL *</div><input required type="url" placeholder="https://..." value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="doc-url" className={inp} /></label>
        )}
        {mode === "paste" && (
          <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Content / notes for AI *</div><textarea required rows={7} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="doc-content" className={inp + " resize-none"} placeholder="Paste the policy text, playbook, or summary the AI should answer questions from." /></label>
        )}
        {mode === "upload" && (
          <label className="block mb-3 cursor-pointer" data-testid="doc-file-label">
            <div className="text-xs uppercase text-slate-500 mb-1">File * (PDF, DOCX, TXT, MD · max 8MB)</div>
            <div className="border border-dashed border-[#2a3447] rounded-lg p-4 flex items-center gap-3 hover:border-[color:var(--accent)]">
              <Upload size={18} className="text-slate-400" />
              <span className="text-sm text-slate-300 truncate">{file ? file.name : "Choose file…"}</span>
            </div>
            <input hidden type="file" accept=".pdf,.docx,.txt,.md" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file" />
          </label>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Cancel</button>
          <button type="submit" disabled={busy} data-testid="doc-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm disabled:opacity-50" style={{ background: "var(--accent)" }}>{busy ? "Saving…" : "Add"}</button>
        </div>
      </form>
    </div>
  );
}
